import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Plus, Edit, ChevronLeft, Search, Copy, Trash2 } from 'lucide-react';
import { RecordActions } from '../../../components/common/RecordActions';
import { RecordViewDialog } from '../../../components/common/RecordViewDialog';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { psAPI, companyAPI, controllingAPI } from '../../../services/api';
import { toast } from 'sonner';

const ProjectTemplates = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('projecttemplates');
  const [viewItem, setViewItem] = useState(null);
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showCreateFromTemplate, setShowCreateFromTemplate] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [projectProfiles, setProjectProfiles] = useState([]);
  const [wbsProfiles, setWbsProfiles] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [controllingAreas, setControllingAreas] = useState([]);

  const [formData, setFormData] = useState({
    template_id: '', name: '', description: '', template_type: 'FULL',
    project_profile_id: '', wbs_profile_id: '', network_profile_id: '',
    project_type: 'INTERNAL', default_duration_days: 30, wbs_elements: [],
    is_public: true, status: 'Active'
  });

  const [newWBS, setNewWBS] = useState({
    template_wbs_id: '', name: '', description: '', parent_template_wbs_id: '',
    level: 1, sort_key: '', is_account_assignment_element: true,
    is_planning_element: true, is_billing_element: false,
    default_duration_days: 0, start_offset_days: 0
  });

  const [createProjectData, setCreateProjectData] = useState({
    template_id: '', project_id: '', project_name: '',
    start_date: new Date().toISOString().split('T')[0],
    company_code_id: '', controlling_area_id: '', profit_center_id: '',
    copy_wbs_structure: true, adjust_dates_from_start: true
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [templatesRes, ppRes, wpRes, ccRes, caRes] = await Promise.all([
        psAPI.getTemplates(),
        psAPI.getProjectProfiles(),
        psAPI.getWBSProfiles(),
        companyAPI.getCompanyCodes(),
        controllingAPI.getControllingAreas()
      ]);
      setTemplates(templatesRes.data);
      setProjectProfiles(ppRes.data.filter(p => p.status === 'Active'));
      setWbsProfiles(wpRes.data.filter(p => p.status === 'Active'));
      setCompanyCodes(ccRes.data);
      setControllingAreas(caRes.data);
    } catch (error) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleCreate = () => {
    setEditMode(false);
    setFormData({ template_id: '', name: '', description: '', template_type: 'FULL', project_profile_id: '', wbs_profile_id: '', network_profile_id: '', project_type: 'INTERNAL', default_duration_days: 30, wbs_elements: [], is_public: true, status: 'Active' });
    setShowDialog(true);
  };

  const handleEdit = (template) => {
    setEditMode(true);
    setFormData({ ...template, description: template.description || '', project_profile_id: template.project_profile_id || '', wbs_profile_id: template.wbs_profile_id || '', network_profile_id: template.network_profile_id || '', wbs_elements: template.wbs_elements || [] });
    setShowDialog(true);
  };

  const addWBS = () => {
    if (!newWBS.template_wbs_id || !newWBS.name) { toast.error('ID and Name required'); return; }
    if (formData.wbs_elements.some(w => w.template_wbs_id === newWBS.template_wbs_id)) { toast.error('ID exists'); return; }
    setFormData({ ...formData, wbs_elements: [...formData.wbs_elements, {...newWBS}] });
    setNewWBS({ template_wbs_id: '', name: '', description: '', parent_template_wbs_id: '', level: 1, sort_key: '', is_account_assignment_element: true, is_planning_element: true, is_billing_element: false, default_duration_days: 0, start_offset_days: 0 });
  };

  const removeWBS = (index) => {
    setFormData({ ...formData, wbs_elements: formData.wbs_elements.filter((_, i) => i !== index) });
  };

  const handleSubmit = async () => {
    if (!formData.template_id || !formData.name) { toast.error('Fill required fields'); return; }
    try {
      if (editMode) { await psAPI.updateTemplate(formData.template_id, formData); toast.success('Updated'); }
      else { await psAPI.createTemplate(formData); toast.success('Created'); }
      setShowDialog(false); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const openCreateFromTemplate = (template) => {
    setSelectedTemplate(template);
    setCreateProjectData({ template_id: template.template_id, project_id: '', project_name: '', start_date: new Date().toISOString().split('T')[0], company_code_id: '', controlling_area_id: '', profit_center_id: '', copy_wbs_structure: true, adjust_dates_from_start: true });
    setShowCreateFromTemplate(true);
  };

  const handleCreateFromTemplate = async () => {
    if (!createProjectData.project_id || !createProjectData.project_name || !createProjectData.company_code_id || !createProjectData.controlling_area_id) {
      toast.error('Fill required fields'); return;
    }
    try {
      await psAPI.createProjectFromTemplate(createProjectData.template_id, createProjectData);
      toast.success('Project created from template');
      setShowCreateFromTemplate(false);
      navigate('/ps/projects');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const filteredTemplates = templates.filter(t => t.template_id.toLowerCase().includes(searchTerm.toLowerCase()) || t.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="templates-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/ps')}><ChevronLeft className="h-5 w-5" /></Button>
        <div className="flex-1"><h1 className="text-2xl font-bold">Project Templates</h1><p className="text-gray-500">Reusable project structures</p></div>
        {writable && (<Button onClick={handleCreate}><Plus className="h-4 w-4 mr-2" />New Template</Button>)}
      </div>

      <Card><CardContent className="pt-6"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /><Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div></CardContent></Card>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Template ID</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Project Type</TableHead><TableHead>WBS Elements</TableHead><TableHead>Usage</TableHead><TableHead>Status</TableHead><TableHead className="w-[120px]">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={8} className="text-center py-8">Loading...</TableCell></TableRow> :
            filteredTemplates.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">No templates</TableCell></TableRow> :
            filteredTemplates.map((t) => (
              <TableRow key={t.template_id}>
                <TableCell className="font-medium">{t.template_id}</TableCell>
                <TableCell>{t.name}</TableCell>
                <TableCell><Badge variant="outline">{t.template_type}</Badge></TableCell>
                <TableCell>{t.project_type}</TableCell>
                <TableCell>{t.wbs_element_count || 0}</TableCell>
                <TableCell>{t.usage_count || 0}</TableCell>
                <TableCell><Badge variant={t.status === 'Active' ? 'default' : 'secondary'}>{t.status}</Badge></TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <RecordActions doctype="projecttemplates" onView={() => setViewItem(t)} onEdit={() => handleEdit(t)} />
                    <Button variant="ghost" size="icon" onClick={() => openCreateFromTemplate(t)} title="Create Project"><Copy className="h-4 w-4 text-orange-600" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Template Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editMode ? 'Edit' : 'Create'} Project Template</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Template ID *</Label><Input value={formData.template_id} onChange={(e) => setFormData({...formData, template_id: e.target.value.toUpperCase()})} disabled={editMode} /></div>
              <div className="space-y-2"><Label>Name *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} /></div>
              <div className="col-span-2 space-y-2"><Label>Description</Label><Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} /></div>
              <div className="space-y-2"><Label>Template Type</Label><Select value={formData.template_type} onValueChange={(v) => setFormData({...formData, template_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="PROJECT">Project Only</SelectItem><SelectItem value="WBS_STRUCTURE">WBS Structure</SelectItem><SelectItem value="FULL">Full</SelectItem></SelectContent></Select></div>
              <div className="space-y-2"><Label>Project Type</Label><Select value={formData.project_type} onValueChange={(v) => setFormData({...formData, project_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="INTERNAL">Internal</SelectItem><SelectItem value="CUSTOMER">Customer</SelectItem><SelectItem value="INVESTMENT">Investment</SelectItem><SelectItem value="MAINTENANCE">Maintenance</SelectItem><SelectItem value="RD">R&D</SelectItem></SelectContent></Select></div>
              <div className="space-y-2"><Label>Project Profile</Label><Select value={formData.project_profile_id || "__none__"} onValueChange={(v) => setFormData({...formData, project_profile_id: v === "__none__" ? "" : v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="__none__">None</SelectItem>{projectProfiles.map(p => <SelectItem key={p.profile_id} value={p.profile_id}>{p.profile_id}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Default Duration (days)</Label><Input type="number" min="1" value={formData.default_duration_days} onChange={(e) => setFormData({...formData, default_duration_days: parseInt(e.target.value) || 30})} /></div>
              <div className="space-y-2"><Label>Status</Label><Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem><SelectItem value="Draft">Draft</SelectItem></SelectContent></Select></div>
              <div className="flex items-center gap-2"><Switch checked={formData.is_public} onCheckedChange={(v) => setFormData({...formData, is_public: v})} /><Label>Public Template</Label></div>
            </div>

            {/* WBS Structure */}
            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">WBS Structure</h3>
              <div className="grid grid-cols-4 gap-2 mb-4 p-3 bg-gray-50 rounded">
                <div className="space-y-1"><Label className="text-xs">WBS ID *</Label><Input value={newWBS.template_wbs_id} onChange={(e) => setNewWBS({...newWBS, template_wbs_id: e.target.value.toUpperCase()})} placeholder="PHASE1" /></div>
                <div className="space-y-1"><Label className="text-xs">Name *</Label><Input value={newWBS.name} onChange={(e) => setNewWBS({...newWBS, name: e.target.value})} /></div>
                <div className="space-y-1"><Label className="text-xs">Level</Label><Input type="number" min="1" value={newWBS.level} onChange={(e) => setNewWBS({...newWBS, level: parseInt(e.target.value) || 1})} /></div>
                <div className="flex items-end">{writable && (<Button size="sm" onClick={addWBS}><Plus className="h-4 w-4 mr-1" />Add</Button>)}</div>
              </div>
              {formData.wbs_elements.length > 0 && (
                <Table>
                  <TableHeader><TableRow><TableHead>WBS ID</TableHead><TableHead>Name</TableHead><TableHead>Level</TableHead><TableHead>Duration</TableHead><TableHead className="w-[60px]"></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {formData.wbs_elements.map((w) => (
                      <TableRow key={w.template_wbs_id}><TableCell className="font-medium">{w.template_wbs_id}</TableCell><TableCell>{w.name}</TableCell><TableCell>{w.level}</TableCell><TableCell>{w.default_duration_days} days</TableCell><TableCell><Button variant="ghost" size="icon" onClick={() => removeWBS(formData.wbs_elements.indexOf(w))}><Trash2 className="h-4 w-4 text-red-500" /></Button></TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button><Button onClick={handleSubmit}>{editMode ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Project from Template */}
      <Dialog open={showCreateFromTemplate} onOpenChange={setShowCreateFromTemplate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Create Project from Template</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-500">Template: <strong>{selectedTemplate?.name}</strong></p>
            <div className="space-y-2"><Label>Project ID *</Label><Input value={createProjectData.project_id} onChange={(e) => setCreateProjectData({...createProjectData, project_id: e.target.value.toUpperCase()})} placeholder="PRJ-2025-001" /></div>
            <div className="space-y-2"><Label>Project Name *</Label><Input value={createProjectData.project_name} onChange={(e) => setCreateProjectData({...createProjectData, project_name: e.target.value})} /></div>
            <div className="space-y-2"><Label>Start Date *</Label><Input type="date" value={createProjectData.start_date} onChange={(e) => setCreateProjectData({...createProjectData, start_date: e.target.value})} /></div>
            <div className="space-y-2"><Label>Company Code *</Label><Select value={createProjectData.company_code_id} onValueChange={(v) => setCreateProjectData({...createProjectData, company_code_id: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{companyCodes.map(cc => <SelectItem key={cc.company_code_id} value={cc.company_code_id}>{cc.company_code_id} - {cc.company_code_name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Controlling Area *</Label><Select value={createProjectData.controlling_area_id} onValueChange={(v) => setCreateProjectData({...createProjectData, controlling_area_id: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{controllingAreas.map(ca => <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>{ca.controlling_area_id} - {ca.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2"><Switch checked={createProjectData.copy_wbs_structure} onCheckedChange={(v) => setCreateProjectData({...createProjectData, copy_wbs_structure: v})} /><Label className="text-sm">Copy WBS Structure</Label></div>
              <div className="flex items-center gap-2"><Switch checked={createProjectData.adjust_dates_from_start} onCheckedChange={(v) => setCreateProjectData({...createProjectData, adjust_dates_from_start: v})} /><Label className="text-sm">Adjust Dates</Label></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreateFromTemplate(false)}>Cancel</Button><Button onClick={handleCreateFromTemplate}>Create Project</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default ProjectTemplates;
